import { Close as DiscardIcon } from '@mui/icons-material';
import { Chip, Grid, IconButton, InputBaseProps, MenuItem, Paper } from '@mui/material';
import { SimplePaletteColorOptions } from '@mui/material/styles';
import { TFunction } from 'i18next';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import ChipInput, { ChipRendererArgs } from '@/components/chip-filter/chip-input';
import { useTranslation } from '@/hooks';
import * as Types from '@/types';

const useStyles = makeStyles()({
  suggestionsContainerOpen: {
    position: 'absolute',
    maxHeight: 300,
    overflowY: 'scroll',
    zIndex: 10,
  },
  textField: {
    width: '100%',
  },
  discardGrid: {
    direction: 'rtl',
  },
  discardButton: {
    padding: 8,
    '@media print': {
      display: 'none',
    },
  },
});

/**
 * This type describes a field, and the possible options for that field.
 * So if we have the theoretical type `Car { engine: boolean, wheels: number, doors: { left: boolean, right: boolean } } `
 * Then this could describe any non object key like, `engine | wheels | right | left`
 * So choosing the amount of wheels could be
 * ```
 * {
 *  name: "Wheels",
 *  key: "wheels"
 *  values: [{
 *    name: "Normal",
 *    value: 4
 *    }, {
 *    name: "Maybe a bike?",
 *    value: 2,
 *  }]
 * }
 * ```
 */
type LeafCategory<Key, Value> = {
  name: string;
  key: Key;
  values: Array<{
    name: string;
    value: Value;
    colors?: SimplePaletteColorOptions;
  }>;
};

// This type takes in an object an goes through all child properties, if they are an object type we generate a SuggestionCategory from below, otherwise we use a SuggestionOptions
/**
 * This helper type goes through all keys of an object, and checks if they're objects
 *  * If a child is an object we create a subcategory
 *  * Otherwise we prepare to setup a LeftCategory to contain possible values for a field
 */
type CategoriesAndLeafs<T extends {}> = {
  [Subkey in keyof T]?: T[Subkey] extends object ? Category<Subkey, T[Subkey]> : LeafCategory<Subkey, T[Subkey]>;
};

/**
 * A helper type to determine the shape of allowed suggestion categories for a given object type
 * For a type like: `Car { engine: boolean, wheels: number, doors: { left: boolean, right: boolean } } `
 * Youd be able to write the following typechecked suggestions for selecting the left doors existence:
 * ```
 * const s: Suggestions<Car> = [
 *  {
 *    name: 'Doors',
 *    key: 'doors',
 *    options: [
 *      {
 *        name: 'Left',
 *        key: 'left',
 *        values: [
 *          {
 *            name: 'Exists',
 *            value: true,
 *          },
 *          { name: 'Needs replacement', value: false },
 *        ],
 *      },
 *    ],
 *  },
 *];
 * ```
 */
export type Suggestions<Value extends {}> = Array<
  NonNullable<CategoriesAndLeafs<Value>[keyof CategoriesAndLeafs<Value>]>
>;

// A category so in this example object { a: { b: { c: "High" | "Low" } }, d: number }, this would describe a and b, but not c and d since they are non objects, and therefore not a "category" of autosuggest options
type Category<Key, Value extends {}> = {
  name: string;
  key: Key;
  // A collection of strings for colors
  colors?: { normal: string; selected?: string; hovered?: string };
  // We take the values of the CategoryOptions for this object, then move all those values into an array, since we might want multiple choices for one key, or simply ignore some keys
  options?: Array<NonNullable<CategoriesAndLeafs<Value>[keyof CategoriesAndLeafs<Value>]>>;
};

interface Chip {
  name: string;
  highlighted?: string;
  colors?: { normal: string; selected?: string; hovered?: string };
  removeAssociatedFilter: () => void;
}

// A derivative of the Suggestion types, where all fields are either other filter objects, or an array of selected values for a key
export type ChosenOptions<T extends object> = {
  [Subkey in keyof T]?: T[Subkey] extends object ? ChosenOptions<T[Subkey]> : Array<T[Subkey]>;
};

// For highlights, we extend with an array of substrings that are either highligted or not
type HighlightExtension = { highlight: Array<{ isHighlighted: boolean; text: string }> };

const findHighlights = <T extends { name: string }>(
  input: readonly T[],
  query: string,
): Array<T & HighlightExtension> => {
  if (input.length === 0) {
    return [];
  }

  const keywords = query
    .split(' ')
    .filter((s) => s.length)
    .map((s) => [s, new RegExp(s, 'i')] as const);

  if (keywords.length === 0) {
    return input.map((o) => ({ highlight: [{ isHighlighted: false, text: o.name }], ...o }));
  }

  const filteredOptions = input.filter(({ name }) => keywords.every(([_, k]) => k.test(name)));

  // Then for all the options we know match the input, we split them on all keywords they contain and mark the matching parts as highligted

  return filteredOptions.map((option): T & HighlightExtension => {
    const highlightedOption = { ...option, highlight: [{ isHighlighted: false, text: option.name }] };

    for (const [word, regex] of keywords) {
      const newHighlights: HighlightExtension['highlight'] = [];
      for (const { isHighlighted, text } of highlightedOption.highlight) {
        // This section is already split so we ignore it
        if (isHighlighted) {
          newHighlights.push({ isHighlighted, text });
          continue;
        }

        // If we're here this section is unsplit, so we try to split it with the current keyword

        const sections = text.split(regex);

        // If we only get one section back, this keyword was not in this substring, so we simply continue with another word
        if (sections.length === 1) {
          newHighlights.push({ isHighlighted, text });
          continue;
        }

        // Since we split on the entire keyword, this means this word is present between every element of the section array

        for (const text of sections) {
          newHighlights.push({ isHighlighted: false, text });
          newHighlights.push({ isHighlighted: true, text: word });
        }

        // We needed to add "section word section word section", but added one word too many in the loop, so we remove the last one
        newHighlights.pop();
      }

      highlightedOption.highlight = newHighlights;
    }

    return highlightedOption;
  });
};

export interface Properties<T extends object> {
  onSuggestions: (filter: ChosenOptions<T>) => void;
  suggestionBase: Suggestions<T>;
  initialSelection?: ChosenOptions<T>;
  placeholderText?: string;
  /**
   * We cannot derive if the type contains a "search" key at runtime, so we enforce a constant if the key exists at typecheck, just trust what tsc recommends, it can derive this field from the suggestion schema
   */
  supportsSearch: T extends { search: string } ? true : false;
}

const AutosuggestChipInput = <T extends object>(props: Properties<T>) => {
  const { classes, theme } = useStyles();
  const [t] = useTranslation('shared');

  const [isFocused, setIsFocused] = useState(false);
  const [chips, setChips] = useState<Chip[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<null | number>(null);
  const [textFieldInput, setTextFieldInput] = useState('');
  // The "chain" of selections we've made, like in the Car type we might've selected ["doors", "right"] (not selected as strings tho)
  const [selectionChain, setSelectionChain] = useState<
    Array<(Category<any, string> | LeafCategory<any, string>) & HighlightExtension>
  >([]);
  const selectedOptions = useRef<ChosenOptions<T>>(props.initialSelection ?? {});

  // Other code might add filters, in that case we ensure those chips exist
  const initialSelection = useRef<ChosenOptions<T> | undefined>();
  useEffect(() => {
    if (!props.initialSelection) {
      return;
    }

    let chosen: ChosenOptions<T>;
    if (initialSelection.current) {
      chosen = getNewOptions(initialSelection.current, props.initialSelection);
    } else {
      initialSelection.current = props.initialSelection;
      chosen = props.initialSelection;
    }

    const newChips = outerChipSelect(
      chosen,
      selectedOptions.current,
      props.suggestionBase,
      () => props.onSuggestions({ ...selectedOptions.current }),
      t,
    );
    const combinedChips = [...chips];

    for (const chip of newChips) {
      // If two chips have the same text value and highligted section, they must represent the same value
      if (chips.some((existing) => existing.name === chip.name && existing.highlighted === chip.highlighted)) {
        continue;
      }
      combinedChips.push(chip);
    }

    setChips(combinedChips);
  }, [props.initialSelection]);

  // We store references to all the suggestion divs in the autosuggest box, so we can scroll to the current selected one if it exists the screen
  const suggestionRef = useRef<Array<HTMLDivElement | null>>([]);

  const currentSuggestion = useRef<Types.Suggestion | undefined>(undefined);

  // We take the content of the last suggestion in the chain, or if theres no chain, the root
  const suggestions = useMemo(() => {
    let last = selectionChain[selectionChain.length - 1];
    if (last) {
      if ('options' in last && last.options) {
        return last.options;
      }
      if ('values' in last && last.values) {
        return last.values;
      }
    }
    return props.suggestionBase ?? [];
  }, [selectionChain, props.suggestionBase]);

  const filteredSuggestions = useMemo(() => {
    const sections = textFieldInput.split(':');
    const query = (sections[sections.length - 1] ?? '').trim();

    const typedSuggestions = suggestions as typeof props.suggestionBase;
    const highlights = findHighlights(typedSuggestions!, query);
    if (highlights.length !== 0) {
      setSelectedSuggestion(0);
    }
    return highlights;
  }, [suggestions, textFieldInput]);

  const renderChip = ({ chip, isFocused, isDisabled, handleClick, handleDelete }: ChipRendererArgs<Chip>) => {
    const colors = chip.colors ?? { normal: undefined, selected: theme.palette.primary.main, hovered: undefined };
    // Some theme colors are just set to "blue" this is bad, so we replace that with primary
    if (colors.selected === 'blue') {
      colors.selected = theme.palette.primary.main;
    }
    return (
      <Chip
        style={{
          pointerEvents: isDisabled ? 'none' : undefined,
          backgroundColor: isFocused ? colors.selected ?? colors.normal : colors.normal,
          cursor: isFocused ? 'text' : 'pointer',
          // If we're selected and have no selected color, we lower opacity
          opacity: isFocused && colors.selected === undefined ? 0.6 : 1,
        }}
        onClick={handleClick}
        onDelete={handleDelete}
        label={
          <p>
            {chip.highlighted && <b>{chip.highlighted}: </b>}
            {chip.name}
          </p>
        }
      />
    );
  };

  const didSelectSuggestion = (selectedSuggestion: number) => {
    const selected = filteredSuggestions[selectedSuggestion];

    if (selected === undefined) {
      // Either the user wanted to search(if allowed) for a keyword, or they wanna go a level up
      if (selectionChain.length === 0 && props.supportsSearch) {
        const selected = selectedOptions.current as unknown as { search?: string[] };
        selected.search ??= [];
        selected.search = selected.search ?? [];
        selected.search.push(textFieldInput);
        const newChip: Chip = {
          name: textFieldInput,
          highlighted: t('shared:search', { defaultValue: 'Search' }),
          removeAssociatedFilter: () => {
            selected.search = selected.search?.filter((k) => k !== textFieldInput);
          },
        };
        setChips([...chips, newChip]);
        setTextFieldInput('');
      } else if (selectionChain.length !== 0) {
        setSelectionChain(selectionChain.slice(0, -1));
      }

      props.onSuggestions({ ...selectedOptions.current });
    } else if ('options' in selected || 'values' in selected) {
      // Then we have new options to present
      const selectedTyped = selected as (typeof selectionChain)[0];
      const newChain = [...selectionChain, selectedTyped];
      setSelectionChain(newChain);
      setTextFieldInput(newChain.map((s) => s.name).join(': ') + ': ');
    } else if ('value' in selected) {
      // An option has been selected, so we add it to the filter using the chain of categories, after that we can construct a new chip with highligts from the chain, and a function to remove the filter again

      // We then go through the chain and set the value we just selected on the filter
      let target: Record<string, unknown> = selectedOptions.current;

      // The last part of the chain selects the array of values, where every other selects other objects with filters on
      const last = selectionChain[selectionChain.length - 1];
      const previous = selectionChain.slice(0, selectionChain.length - 1);
      for (const { key } of previous) {
        let newTarget = target[key] as Record<string, unknown> | undefined;
        if (!newTarget) {
          newTarget = {};
          target[key] = newTarget;
        }
        target = newTarget;
      }

      // We now have the category object, so now we just need the last value to find the key to the array of filter objects

      if (Array.isArray(target[last.key])) {
        (target[last.key] as unknown[]).push(selected.value);
      } else {
        target[last.key] = [selected.value];
      }

      const newChip: Chip = {
        name: selected.name,
        highlighted:
          selectionChain
            .map((s) => s.name)
            .reduce<string | null>((total, element) => (total === null ? element : `${total}:${element}`), null) ?? '',
        removeAssociatedFilter: () => {
          const values = target[last.key];
          if (!Array.isArray(values)) {
            return;
          }

          const filteredValues = values.filter((v) => v !== selected.value);
          if (!filteredValues.length) {
            delete target[last.key];
          } else {
            target[last.key] = filteredValues;
          }
        },
        colors: selected.colors,
      };
      setChips([...chips, newChip]);
      setSelectionChain([]);
      setTextFieldInput('');

      props.onSuggestions({ ...selectedOptions.current });
    }
  };

  const handleOnInputEnter: InputBaseProps['onKeyDown'] = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Was this enter to select an option
    if (selectedSuggestion !== null) {
      didSelectSuggestion(selectedSuggestion);
      event.preventDefault();
    }
  };

  const handleDeleteChip = (chip: Chip, index: number) => {
    chips.splice(index, 1);
    const newChips = [...chips];

    setChips(newChips);
    chip.removeAssociatedFilter();
    props.onSuggestions({ ...selectedOptions.current });
  };

  const clear = () => {
    setChips([]);
    setTextFieldInput('');
    if (props.onSuggestions) {
      currentSuggestion.current = undefined;
      selectedOptions.current = {};
      props.onSuggestions({});
    }
  };

  const scrollSuggestionIntoView = (selected: number) => suggestionRef.current[selected]?.scrollIntoView(false);

  const onKeyDown: InputBaseProps['onKeyDown'] = (e) => {
    if (e.key === 'ArrowUp') {
      if (selectedSuggestion !== null) {
        const selected = Math.max(0, selectedSuggestion - 1);
        setSelectedSuggestion(selected);
        scrollSuggestionIntoView(selected);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      if (selectedSuggestion !== null) {
        const selected = Math.min(filteredSuggestions.length - 1, selectedSuggestion + 1);
        setSelectedSuggestion(selected);
        scrollSuggestionIntoView(selected);
      }
      return;
    }
    if (e.key === 'Enter') {
      handleOnInputEnter(e);
      return;
    }
    // Normal text input
  };

  const onTextFieldChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    // When 2 categories have been selected we do "a: b: c"
    // This simply counts the amount of :'s, since if the user has backspaced to "a: b" they probably want to change that choice
    const { value } = e.target;
    const sections = value.split(':');
    const splits = sections.length - 1;
    if (splits !== selectionChain.length) {
      setSelectionChain(selectionChain.slice(0, splits));
    }
    setTextFieldInput(value);
  };

  const focusStates = useRef({ inputHover: false, inputEditing: false, suggestionHover: false });
  const chipDivRef = useRef<HTMLDivElement | null>(null);
  const suggestionDivRef = useRef<HTMLDivElement | null>(null);
  // We only unfocus is the mouse is outside both divs, and the input text field is unfocused
  const doFocusCheck = useMemo(
    () => () => {
      const { inputHover, inputEditing, suggestionHover } = focusStates.current;
      if (!inputHover && !inputEditing && !suggestionHover) {
        setIsFocused(false);
      }
    },
    [focusStates, setIsFocused],
  );

  useEffect(() => {
    if (chipDivRef.current) {
      chipDivRef.current.addEventListener('mouseenter', () => (focusStates.current.inputHover = true));
      chipDivRef.current.addEventListener('mouseleave', () => {
        focusStates.current.inputHover = false;
        doFocusCheck();
      });
    }
    if (suggestionDivRef.current) {
      suggestionDivRef.current.addEventListener('mouseenter', () => (focusStates.current.suggestionHover = true));
      suggestionDivRef.current.addEventListener('mouseleave', () => {
        focusStates.current.suggestionHover = false;
        doFocusCheck();
      });
    }
  }, []);

  return (
    <Grid container>
      <Grid item xs={11}>
        {/* Input */}
        <ChipInput
          allowDuplicates={false}
          clearInputValueOnChange={false}
          renderChip={renderChip}
          inputValue={textFieldInput}
          chips={chips}
          placeholder={
            props.placeholderText
              ? props.placeholderText
              : t(['shared:filterValues'], { defaultValue: 'Filter values..' })
          }
          onAdd={(chip) => setChips([...chips, chip])}
          onDelete={handleDeleteChip}
          InputProps={{
            onKeyDown,
            value: textFieldInput,
            onChange: (e) => onTextFieldChange(e),
            onFocus: () => {
              setIsFocused(true);
              focusStates.current.inputEditing = true;
            },
            onBlur: () => {
              focusStates.current.inputEditing = false;
              doFocusCheck();
            },
          }}
          ref={(inputRef) => (chipDivRef.current = inputRef)}
        />
        {/* Suggestions */}
        <div style={{ display: isFocused ? 'unset' : 'none' }} ref={(divRef) => (suggestionDivRef.current = divRef)}>
          <Paper square className={classes.suggestionsContainerOpen}>
            {filteredSuggestions.map((suggestion, index) => (
              <MenuItem
                className="chipInputOption"
                ref={(r) => (suggestionRef.current[index] = r)}
                selected={selectedSuggestion === index}
                component="div"
                key={index}
                onClick={() => didSelectSuggestion(index)}
              >
                {suggestion.highlight?.map(({ isHighlighted, text }, index) =>
                  isHighlighted ? (
                    <strong key={String(index)} style={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                      {text}
                    </strong>
                  ) : (
                    <span key={String(index)} style={{ fontWeight: 500 }}>
                      {text}
                    </span>
                  ),
                ) ?? <span style={{ color: theme.palette.primary.main, fontWeight: 500 }}>{suggestion.name}</span>}
              </MenuItem>
            ))}
            {filteredSuggestions.length === 0 &&
              // If we have selected an option, but have none we show a message letting the user go one level up
              (selectionChain.length ? (
                <MenuItem
                  selected
                  component="div"
                  className="chipInputOption"
                  onClick={() => didSelectSuggestion(Number.MAX_VALUE)}
                >
                  {t('shared:noSuggestionsInCategory', {
                    defaultValue: 'There are no suggestions in this category, press enter to go back',
                  })}
                </MenuItem>
              ) : props.supportsSearch ? (
                // If this field allows search, we show an option that creats a "search:" chip
                <MenuItem
                  selected
                  component="div"
                  className="chipInputOption"
                  onClick={() => didSelectSuggestion(Number.MAX_VALUE)}
                >
                  {t('shared:searchFor', {
                    defaultValue: 'Search for',
                  })}
                  : "<b>{textFieldInput}</b>"
                </MenuItem>
              ) : (
                // If we've got no search, and no matches, we tell the user
                <MenuItem selected component="div">
                  {t('shared:noResults', {
                    defaultValue: 'Could not find anything for',
                  })}
                  : "<b>{textFieldInput}</b>"
                </MenuItem>
              ))}
          </Paper>
        </div>
      </Grid>
      <Grid item xs={1} className={classes.discardGrid}>
        <IconButton
          onClick={clear}
          disabled={!textFieldInput.length && !chips.length}
          className={classes.discardButton}
          size="large"
        >
          <DiscardIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

/** For a chosen value, create the chip along with a function that removes this choice from the chosen options when deleted */
const selectEndChip = (
  propSelections: unknown[],
  currentlySelected: unknown[],
  options: LeafCategory<unknown, unknown>['values'],
  path: string[],
  setSelected: (values: unknown[]) => void,
) => {
  const chips: Chip[] = [];
  for (const selectedValue of propSelections) {
    const option = options.find((o) => o.value === selectedValue);
    if (!option) {
      console.error('Could not find any suggestion for', selectedValue);
      continue;
    }
    currentlySelected.push(option.value);
    chips.push({
      name: option.name,
      removeAssociatedFilter: () => setSelected(currentlySelected.filter((v) => v !== selectedValue)),
      // eslint-disable-next-line @blackbird/reduce-initial-value
      highlighted: path.length ? path.reduce((total, segment) => `${total}:${segment}`) : undefined,
    });
  }
  return chips;
};

/** Generate chips for all selected options in Category of options */
const selectChips = (
  propSelections: object,
  currentlySelected: object,
  path: string[],
  onChange: () => void,
  categories?: Array<Category<unknown, object> | LeafCategory<unknown, object>>,
) => {
  if (!categories) {
    return [];
  }
  const chips: Chip[] = [];
  for (const [key, value] of Object.entries(propSelections)) {
    const type = categories.find((c) => c.key === key);
    if (!type) {
      console.error('Failed to find suggestions for category key', key);
      continue;
    }
    if ('values' in type) {
      // Then we just need to select the values
      (currentlySelected as any)[key] ??= [];
      chips.push(
        ...selectEndChip(value, (currentlySelected as any)[key], type.values, [...path, type.name], (newValue) => {
          (currentlySelected as any)[key] = newValue;
          onChange();
        }),
      );
    } else {
      if (typeof value !== 'object') {
        console.error(`Got to key ${key} and expected to find object for next level of suggestion, but found`, value);
        continue;
      }
      (currentlySelected as any)[key] ??= {};
      chips.push(...selectChips(value, (currentlySelected as any)[key], [...path, type.name], onChange, type.options));
    }
  }

  return chips;
};

/** For selected options in `propSelections` generate all the appropriate chips */
const outerChipSelect = <T extends {}>(
  propSelections: ChosenOptions<T>,
  currentlySelected: ChosenOptions<T>,
  suggestions: Suggestions<T>,
  onChange: () => void,
  t: TFunction,
) => {
  const chips: Chip[] = [];
  for (const [key, value] of Object.entries(propSelections)) {
    const type = suggestions.find((c) => c.key === key);
    if (!type) {
      // An outer key called "search" does not require a suggestion to make a chip
      if (key === 'search' && Array.isArray(value)) {
        const castedSelected = currentlySelected as unknown as { search?: string[] };
        let currentList = castedSelected.search;
        if (!currentList) {
          currentList = [];
          castedSelected.search = currentList;
        }
        for (const keyword of value) {
          currentList.push(keyword);
          chips.push({
            name: keyword,
            highlighted: t('shared:search', { defaultValue: 'Search' }),
            removeAssociatedFilter: () => {
              castedSelected.search = castedSelected.search?.filter((k) => k !== keyword);
            },
          });
        }
      } else {
        console.error('Failed to find suggestions for category key', key);
      }
      continue;
    }
    // @ts-ignore
    if (typeof type === 'object' && 'values' in type) {
      // Then we just need to select the values
      (currentlySelected as any)[key] ??= [];
      chips.push(
        ...selectEndChip(value as unknown[], (currentlySelected as any)[key], type.values, [type.name], (newValue) => {
          (currentlySelected as any)[key] = newValue;
          onChange();
        }),
      );
    } else {
      (currentlySelected as any)[key] ??= {};
      if (typeof value !== 'object' || value === null) {
        console.error(`Got to key ${key} and expected to find object for next level of suggestion, but found`, value);
        continue;
      }
      chips.push(...selectChips(value, (currentlySelected as any)[key], [type.name], type.options as any));
    }
  }
  return chips;
};

/** Filter out any values in `changed` that already existed in `original` */
const getNewOptions = <T extends object>(original: ChosenOptions<T>, changed: ChosenOptions<T>): ChosenOptions<T> => {
  const allKeys: [keyof T] = Object.keys(original).concat(Object.keys(changed)) as [keyof T];
  const out: ChosenOptions<T> = {};

  for (const key of allKeys) {
    if (out[key] !== undefined) {
      // We've already set this key
      continue;
    }

    const originalVal = original[key];
    const changedVal = changed[key];

    // New key added, so we add it all
    if (originalVal === undefined && changedVal !== undefined) {
      out[key] = changedVal;
      continue;
    }

    // This key was removed, so we remove it
    if (originalVal !== undefined && changedVal === undefined) {
      continue;
    }

    if (Array.isArray(originalVal) && Array.isArray(changedVal)) {
      const newValues: T[keyof T][] = [];
      for (const val of changedVal) {
        if (originalVal.some((existing) => _.isEqual(val, existing))) {
          // We already have this value
          continue;
        }
        newValues.push(val);
      }

      // If we're here we know that T[keyof T] is not an object, but TS does not know, so we need a lil cast
      out[key] = newValues as any;
      continue;
    }

    const castedOriginal = originalVal as ChosenOptions<T[keyof T] & {}>;
    const castedChanged = changedVal as ChosenOptions<T[keyof T] & {}>;

    // Same as above, TS does not realize that here T[keyof T] must be an object so we can just set it
    out[key] = getNewOptions(castedOriginal, castedChanged) as any;
  }

  return out;
};

export default AutosuggestChipInput;
